import React from 'react'
// libs
import { useSelector, useStore } from 'react-redux'
import { getFormValues } from 'redux-form'
import { useMutation } from '@apollo/client/react'
// hooks
import { useApolloClient, useModalData } from 'hooks'
// components
import { LoaderWithOverlay, Modal } from 'components/UI'
// view components
import Header from 'components/UI/Modal/Header'
import AddResourceForm from './AddResourceForm'
// constants
import { Modals } from 'constants/modals'
import { Forms } from 'constants/forms'
import { OBJECT_ENTITY_NAMES } from 'constants/objects'
// store
import { objectEntitiesSelector } from 'store/reducers/objectEntities'
// graphql
import { RESOURCE_CREATE_MUTATION, RESOURCE_UPDATE_MUTATION } from 'graphql/resource/mutations'
// utils
import get from 'lodash/get'
import { ifElse } from 'utils/fp'
import { isEntity } from 'utils/types'
import { getFieldsList } from 'utils/fields'
import { runPendingUploads } from 'utils/pendingFileUploads'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'
import { getFieldsInitialValues, getFieldValues } from 'utils/form'

const PAPER_PROPS = {
  style: { width: '554px', height: 'calc(100vh - 200px)' }
}

const AddResourceModal = () => {
  const { client } = useApolloClient()

  const {
    modalData: { width, isOpen, entity, refetch, resource },
    hideModal
  } = useModalData({ modalType: Modals.RESOURCE_CREATE_MODAL })

  const store = useStore()

  const objectEntities = useSelector(objectEntitiesSelector)

  const [createMutation] = useMutation(RESOURCE_CREATE_MUTATION)
  const [updateMutation] = useMutation(RESOURCE_UPDATE_MUTATION)

  const [isLoading, setIsLoading] = React.useState(true)
  const [initialValues, setInitialValues] = React.useState({})

  const canModify =
    entity?.currentPermissions?.objectiveModifyResource || entity?.currentPermissions?.actionModifyResource

  const fields = getFieldsList(objectEntities, OBJECT_ENTITY_NAMES.RESOURCE)

  // methods
  const closeModal = () => {
    hideModal()
    setInitialValues({})
    setIsLoading(true)
  }

  const handleCreate = async (data) => {
    try {
      await createMutation({
        variables: {
          input: {
            planActionId: ifElse(isEntity.PlanAction, entity.id)(entity),
            planObjectiveId: ifElse(isEntity.PlanObjective, entity.id)(entity),
            fieldValues: getFieldValues(fields, data)
          }
        }
      })

      await refetch()

      closeModal()
    } catch (error) {
      console.log('[AddResourceModal.handleCreate]: ', parseGraphQLErrorMessages(error))
    }
  }

  const handleUpdate = async (data) => {
    try {
      await updateMutation({
        variables: {
          id: get(resource, 'id'),
          input: {
            fieldValues: getFieldValues(fields, data)
          }
        }
      })

      await refetch()

      closeModal()
    } catch (error) {
      console.log('[AddResourceModal.handleUpdate]: ', parseGraphQLErrorMessages(error))
    }
  }

  const handleSubmit = async (formData) => {
    await runPendingUploads(Forms.RESOURCE_CREATE_FORM)

    const data = getFormValues(Forms.RESOURCE_CREATE_FORM)(store.getState()) || formData

    return resource ? handleUpdate(data) : handleCreate(data)
  }

  const getInitialValues = async () => {
    try {
      if (resource) {
        const initialValues = await getFieldsInitialValues({
          fieldValues: get(resource, 'fieldValueMap', []),
          fields,
          client
        })

        setInitialValues(initialValues)
      }
    } catch (e) {
      console.log('[AddResourceModal.getFieldsInitialValues]: ', parseGraphQLErrorMessages(e))
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    if (isOpen) {
      getInitialValues()
    }
  }, [isOpen])

  return (
    <Modal width={width} isOpen={isOpen} hideModal={closeModal} PaperProps={PAPER_PROPS}>
      <Header handleClearClick={closeModal}>
        {resource ? 'Edit ' : 'Create '}
        Resource
      </Header>
      {isLoading ? (
        <LoaderWithOverlay isLoading />
      ) : (
        <AddResourceForm
          fields={fields}
          canModify={canModify}
          hideModal={closeModal}
          onSubmit={handleSubmit}
          initialValues={initialValues}
        />
      )}
    </Modal>
  )
}

export default AddResourceModal
